import { useState } from 'react';
import { GoogleGenAI, Type } from '@google/genai';
import type { TourAIInput, TourAISuggestion } from '@mondivago/types';

export type { TourAIInput, TourAISuggestion };

const MODELS = ['gemini-2.5-flash', 'gemini-2.0-flash', 'gemini-2.5-flash-lite'];

const RESPONSE_SCHEMA = {
  type: Type.OBJECT,
  properties: {
    // Original
    title: { type: Type.STRING },
    description: { type: Type.STRING },
    duration: { type: Type.STRING },
    physicalDifficulty: { type: Type.STRING, enum: ['easy', 'medium', 'hard'] },
    inclusions: { type: Type.ARRAY, items: { type: Type.STRING } },
    exclusions: { type: Type.ARRAY, items: { type: Type.STRING } },
    faqs: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          question: { type: Type.STRING },
          answer: { type: Type.STRING },
        },
        required: ['question', 'answer'],
        propertyOrdering: ['question', 'answer'],
      },
    },
    meetingInstructions: { type: Type.STRING },
    languages: { type: Type.ARRAY, items: { type: Type.STRING } },
    // SEO
    metaDescription: { type: Type.STRING },
    seoKeywords: { type: Type.ARRAY, items: { type: Type.STRING } },
    // Pricing
    suggestedAdultPrice: { type: Type.NUMBER },
    suggestedChildPrice: { type: Type.NUMBER },
    suggestedInfantPrice: { type: Type.NUMBER },
    marketInsight: { type: Type.STRING },
    // Category
    suggestedCategory: { type: Type.STRING },
    // Attractions
    suggestedAttractions: { type: Type.ARRAY, items: { type: Type.STRING } },
    // Cancellation
    suggestedCancellationPolicy: { type: Type.STRING, enum: ['flexible', 'standard', 'strict'] },
    cancellationReasoning: { type: Type.STRING },
  },
  required: [
    'title',
    'description',
    'duration',
    'physicalDifficulty',
    'inclusions',
    'exclusions',
    'faqs',
    'meetingInstructions',
    'languages',
    'metaDescription',
    'seoKeywords',
    'suggestedAdultPrice',
    'suggestedChildPrice',
    'suggestedInfantPrice',
    'marketInsight',
    'suggestedCategory',
    'suggestedAttractions',
    'suggestedCancellationPolicy',
    'cancellationReasoning',
  ],
  propertyOrdering: [
    'title',
    'description',
    'duration',
    'physicalDifficulty',
    'inclusions',
    'exclusions',
    'faqs',
    'meetingInstructions',
    'languages',
    'metaDescription',
    'seoKeywords',
    'suggestedAdultPrice',
    'suggestedChildPrice',
    'suggestedInfantPrice',
    'marketInsight',
    'suggestedCategory',
    'suggestedAttractions',
    'suggestedCancellationPolicy',
    'cancellationReasoning',
  ],
};

function buildPrompt(input: TourAIInput): string {
  return `You are an expert travel industry content writer. Generate a COMPLETE, accurate listing for a local tour. Return ALL fields in the JSON schema — do not skip any.

## Tour Details
- Location: ${input.location}${input.country ? `, ${input.country}` : ''}
- Type: ${input.tourType}
- Tour description: ${input.description}
${input.duration ? `- Duration: ${input.duration} hours` : ''}
${input.highlights ? `- Highlights: ${input.highlights}` : ''}
${input.maxGroup ? `- Max group size: ${input.maxGroup}` : ''}

## Field Guidelines

### Content Fields
- **title**: Catchy, max 60 chars, SEO-friendly
- **description**: Rich markdown with ## headings and bullet points. 3-4 paragraphs. Engaging, informative, SEO-optimized.
- **duration**: Hours as string, e.g. "3". Match the input duration if provided.
- **physicalDifficulty**: One of easy / medium / hard. Match to tour type (walking=medium, food=easy, hiking=hard).
- **inclusions**: 5-8 specific items (e.g. "Professional guide", "Wine tasting", "Hotel pickup").
- **exclusions**: 3-5 items (e.g. "Hotel drop-off", "Personal expenses").
- **faqs**: 4-5 common traveler questions with clear, helpful answers.
- **meetingInstructions**: 2-3 specific sentences about where and how to meet.
- **languages**: Default to ["English"]. Add more if relevant to location.

### SEO Fields
- **metaDescription**: 1-2 sentences (max 160 chars) — compelling search snippet, includes location and tour type.
- **seoKeywords**: 6-10 relevant keywords/tags for the tour (e.g. ["Rome walking tour", "Colosseum", "Italian history"]).

### Pricing Fields — BE REALISTIC, use real market data for the location
- **suggestedAdultPrice**: Realistic EUR price for adults based on location and tour type.
- **suggestedChildPrice**: Typically 50-70% of adult price. 0 if not suitable for children.
- **suggestedInfantPrice**: Typically 0 for most tours.
- **marketInsight**: One short sentence like "Similar tours in Rome: Walking tours €35-55, Food tours €65-90".

### Category Field
- **suggestedCategory**: The most fitting category name (e.g. "Walking Tours", "Food & Drink", "Cultural", "Adventure", "Boat Tours", "Photography", "Cycling", "Nightlife", "Day Trips", "Multi-day"). Match to the tour type.

### Attractions Field
- **suggestedAttractions**: Array of 2-5 famous attraction/landmark names relevant to this location and tour type. Use real, well-known places.

### Cancellation Field
- **suggestedCancellationPolicy**: "flexible" (24h full refund) for low-commitment tours (walking, food, photography). "standard" (48h) for most tours. "strict" (72h+) for adventure, boat, or multi-day tours.
- **cancellationReasoning**: One short sentence explaining the recommendation.`;
}

const isRetryableError = (msg: string) => {
  const m = msg.toLowerCase();
  return (
    m.includes('high demand') ||
    m.includes('overloaded') ||
    m.includes('unavailable') ||
    m.includes('temporarily') ||
    m.includes('503') ||
    m.includes('429') ||
    m.includes('resource_exhausted') ||
    m.includes('quota') ||
    m.includes('not found') ||
    m.includes('404')
  );
};

function buildConfig(modelName: string) {
  const config: Record<string, unknown> = {
    responseMimeType: 'application/json',
    responseSchema: RESPONSE_SCHEMA,
    maxOutputTokens: 1500,
    temperature: 0.7,
  };
  if (modelName.includes('2.5')) {
    config.thinkingConfig = { thinkingBudget: 0 };
  }
  return config;
}

export function useGeminiAI() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateTourContent = async (input: TourAIInput): Promise<TourAISuggestion | null> => {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY as string | undefined;
    if (!apiKey) {
      setError('Gemini API key is not configured. Add VITE_GEMINI_API_KEY to your .env.local file.');
      return null;
    }

    setIsLoading(true);
    setError(null);

    const prompt = buildPrompt(input);
    const ai = new GoogleGenAI({ apiKey });
    let lastError = '';

    try {
      for (const modelName of MODELS) {
        try {
          const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
            config: buildConfig(modelName),
          });
          const text = (response.text ?? '').trim();
          return JSON.parse(text) as TourAISuggestion;
        } catch (err) {
          lastError = err instanceof Error ? err.message : String(err);
          const isLast = modelName === MODELS[MODELS.length - 1];
          if (isRetryableError(lastError) && !isLast) continue;
          break;
        }
      }

      setError(
        isRetryableError(lastError)
          ? 'AI is currently busy. Please try again in a few seconds.'
          : lastError || 'Failed to generate content. Please try again.'
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { generateTourContent, isLoading, error };
}
